import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { orgTypeServiceUrl } from "../services/dataManager";
import { setOrgStructureData, type OrgStructureState } from "../reducers/orgStructure";
import type {
  OrgStructureItem,
  OrgStructureNameIdEntry,
  OrgUnitsControllerResponse,
  OrgUnitsListRequest,
  SaveOrgUnitPayload,
  GetOrgTypesResponse,
  ReorderOrgStructureRequest,
  ReorderOrgStructureResponse,
  GetAllPositionUsersDataRequest,
  GetAllPositionUsersDataResponse,
} from "@ServerTypes/orgUnits/orgUnits.schema";
import type { Exact } from "@Helpers/types/exact";

/**
 * `orgUnitsControllerResponse.result` is declared `z.unknown()`, so the list route's rows have to be
 * established here. `id` is the only member `orgStructureItemSchema` requires.
 */
const isOrgStructureItem = (value: unknown): value is OrgStructureItem =>
  typeof value === "object" && value !== null && "id" in value && typeof value.id === "string";

export const getOrgstructureData =
  <T extends Exact<OrgUnitsListRequest, T>>(data: T) =>
  async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<OrgUnitsControllerResponse>(
      `${getBackendUrl()}/orgStructure/orgStructureController`,
      data,
    );
    if (response.data.status) {
      const rows = Array.isArray(response.data.result) ? response.data.result.filter(isOrgStructureItem) : [];
      const updatedDataForOrgStructure: OrgStructureState = {
        completeData: rows,
      };

      // Same rows as `completeData` above, walked to build the typeId→typeName and
      // typeName→[{id,name}] side indexes. `typeId` / `type` are optional on the schema and are
      // used as keys unguarded here, so the `!`s are erased. The name/id index is accumulated in
      // its own typed map so the entries are never read back off the state's index signature.
      const entriesByType: Record<string, OrgStructureNameIdEntry[]> = {};
      rows.forEach((data) => {
        if (!Object.hasOwn(updatedDataForOrgStructure, data.typeId!)) {
          updatedDataForOrgStructure[data.typeId!] = data.type;
        }
        entriesByType[data.type!] = [...(entriesByType[data.type!] ?? []), { id: data.id, name: data.name! }];
      });
      Object.assign(updatedDataForOrgStructure, entriesByType);

      dispatch(setOrgStructureData(updatedDataForOrgStructure));
    }
  };

// Dispatches an org-structure insert/update/remove to its dedicated REST endpoint (the former
// /orgStructureController action-dispatcher has been split). `action` selects the endpoint and is
// stripped from the request body; each endpoint validates its own dedicated schema.
const ORG_UNIT_ACTION_PATHS: Record<string, string> = {
  insert: "/orgStructure/insertOrgUnit",
  update: "/orgStructure/updateOrgUnit",
  remove: "/orgStructure/removeOrgUnit",
};
export const getOrgStructure = async (data: SaveOrgUnitPayload) => {
  const { action, ...body } = data;
  const path = ORG_UNIT_ACTION_PATHS[action] || "/orgStructure/removeOrgUnit";
  return await axiosWrapper.post<OrgUnitsControllerResponse>(`${getBackendUrl()}${path}`, body);
};

export const getOrgTypes = async () => {
  const response = await axiosWrapper.post<GetOrgTypesResponse>(orgTypeServiceUrl);
  return response;
};

export const reorderOrgStructure = async <T extends Exact<ReorderOrgStructureRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ReorderOrgStructureResponse>(
    `${getBackendUrl()}/orgStructure/reorderOrgStructure`,
    data,
  );
  return response;
};

export const getAllPositionUsersData = async <T extends Exact<GetAllPositionUsersDataRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetAllPositionUsersDataResponse>(
    `${getBackendUrl()}/orgStructure/getAllPositionUsersData`,
    data,
  );
  return response;
};
